import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Navbar, NavbarItem, NavbarSection } from '@accure/ui';
import { saveAs } from 'file-saver';
import { LifeBuoy } from 'lucide-react';

import { EnvironmentBadge } from '@/components/environment-badge';
import { OrgPicker } from '@/components/layout/org-picker';
import { Link } from '@/components/ui/link';
import { Tooltip } from '@/components/ui/tooltip';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { getOrganizations } from '@/providers/auth/get-organizations';
import { usePageContext } from '@/providers/page-context';
import { DownloadQuickstartGuide } from '@/task-manager/api/mutations/download-quickstart-guide';
import { cls } from '@/utils/cls';
import { siteLabel } from '@/utils/format/customer';

import { LogoSimple } from '../logo-simple';
import { LogoWhiteLabel } from '../logo-white-label';

import { AppHeaderAccount } from './app-header-account';
import { LookerEnvSelector } from './looker-env-selector';
import { MenuToggleButton } from './menu-toggle-button';

export type AppHeaderProps = {
  className?: string;
};

export const AppHeader = ({ className }: AppHeaderProps) => {
  const downloadQuickstartGuide = DownloadQuickstartGuide.useMutation();
  const me = useMe();
  const { organizations } = getOrganizations();

  return (
    <Navbar className={cls('relative border-b px-4 py-1', className)}>
      <div className="grid w-full grid-cols-[auto_auto_1fr_auto] items-center gap-4">
        <MenuToggleButton />
        <div className="flex h-full max-w-32 shrink-0 items-center">
          <Link to="/" data-testid="logo-link">
            {me.whiteLabel ? (
              <LogoWhiteLabel className="block h-10" />
            ) : (
              <LogoSimple className="block h-4" />
            )}
          </Link>
        </div>
        <AppTitle className="justify-self-center py-1.5 text-center" />
        <NavbarSection>
          <EnvironmentBadge />
          <LookerEnvSelector />
          {organizations.length > 1 ? (
            <NavbarItem data-testid="app-header-org-picker">
              <OrgPicker
                organizations={organizations}
                className="border-0 bg-transparent p-0"
              />
            </NavbarItem>
          ) : null}
          <Tooltip
            delay
            position="bottom"
            content="Download the quickstart guide"
          >
            <NavbarItem
              data-testid="quickstart-button"
              onClick={async function () {
                const download = downloadQuickstartGuide.mutateAsync();
                const { file } = await toast.promise(download, {
                  pending: 'Downloading the quickstart guide...',
                  success: undefined,
                  error:
                    'Failed to download the quickstart guide. Please try again.',
                });

                saveAs(file);
              }}
            >
              <LifeBuoy className="size-5 text-zinc-400" />
              <span className="max-sm:hidden">Quickstart</span>
            </NavbarItem>
          </Tooltip>
          <AppHeaderAccount />
        </NavbarSection>
      </div>
    </Navbar>
  );
};

type AppTitleProps = {
  className?: string;
};
function AppTitle({ className }: AppTitleProps) {
  const { asset, vehicle } = useCurrentAsset();
  const me = useMe();
  const { pageConfig, location } = usePageContext();

  const title = useMemo(
    function () {
      if (location.pathname.match(/^\/$/)) {
        return me.whiteLabel
          ? 'SMA LS Cloud'
          : 'Predictive Battery Analytics Platform';
      }

      return pageConfig?.nameFull;
    },
    [me.whiteLabel, location.pathname, pageConfig],
  );

  useEffect(
    function () {
      if (title) {
        if (asset) {
          window.document.title = `${title} | ${siteLabel(asset.fullFrontendId)} | Accure`;
          return;
        }
        if (vehicle) {
          window.document.title = `${title} | ${vehicle.fullFrontendId} | Accure`;
          return;
        }

        window.document.title = `${title} | Accure`;
        return;
      }

      window.document.title = 'Accure';
    },
    [asset, title, vehicle],
  );

  return (
    <h1
      className={cls('text-lg font-medium', className)}
      data-testid="page-title"
    >
      {asset ? (
        <>
          <span className="text-zinc-500">
            {siteLabel(asset.fullFrontendId)} /{' '}
          </span>
          {title}
        </>
      ) : vehicle ? (
        <>
          <span className="text-zinc-500">{vehicle.fullFrontendId} / </span>
          {title}
        </>
      ) : (
        <>{title}</>
      )}
    </h1>
  );
}
